import { useEffect, useMemo, useRef } from 'react';
import * as atlas from 'azure-maps-control';
import { mapAuthOptions, useMapsConfig, useMapStyle } from '@/lib/maps';
import { agoLabel, isStale } from './lib/ais';
import { useRigsMap } from './lib/api';

// Mini-map for the rig spec modal: the rig's scraped AIS position on a small Azure Maps
// canvas. Renders NOTHING when the rig has no position (or maps auth isn't wired) — the
// modal looks exactly as before for position-less rigs. Reuses the cached /api/rigs/map
// payload, so opening a modal costs no extra WWD Api call.
export default function RigMiniMap({ slug }: { slug: string }) {
  const configQuery = useMapsConfig();
  const rigsQuery = useRigsMap();
  const mapStyle = useMapStyle();
  const elRef = useRef<HTMLDivElement>(null);

  const marker = rigsQuery.data?.rigs.find((r) => r.slug === slug);
  const authOptions = useMemo(
    () => (configQuery.data ? mapAuthOptions(configQuery.data) : null),
    [configQuery.data]
  );

  const lat = marker?.lat;
  const lon = marker?.lon;

  useEffect(() => {
    const el = elRef.current;
    if (!el || !authOptions || lat === undefined || lon === undefined) return;

    const map = new atlas.Map(el, {
      center: [lon, lat],
      zoom: 4.5,
      style: mapStyle,
      authOptions,
      showLogo: true,
    });
    map.events.add('ready', () => {
      map.markers.add(new atlas.HtmlMarker({ position: [lon, lat], color: '#3b82f6' }));
    });
    return () => map.dispose();
  }, [authOptions, lat, lon, mapStyle]);

  if (!marker || !authOptions) return null;

  const ago = agoLabel(marker.msgTime);
  const caption = [
    marker.status,
    ago ? `AIS ${ago}` : null,
    isStale(marker.msgTime) ? 'stale' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="mt-4">
      <div
        ref={elRef}
        className="h-44 w-full overflow-hidden rounded-(--radius-sm) border border-border"
        role="img"
        aria-label={`Map position of ${marker.name}`}
      />
      <p className="mt-1 text-xs text-muted">
        {caption ? `${caption} · ` : ''}position via {marker.source ?? 'AIS aggregator'}{' '}
        <span className="italic">(reported)</span>
      </p>
    </div>
  );
}
